import React, { useEffect } from 'react';
import {
	FieldValues,
	useFieldArray,
	UseFieldArrayRemove,
	UseFieldArrayUpdate,
	useFormContext,
} from 'react-hook-form';
import AddButton from '../Button/AddButton';
import FormContainer from './FormContainer';
import FormMultiplierContextProvider from './context/FormMultiplierContext';
import { useFormContainerContext } from '../../Services/FormContainerContext';
import RemoveButton from '../Button/RemoveButton';
import FormMultiplierRemoveButton from './FormMultiplierRemoveButton';

interface IRenderProps {
	index: number;
	update: UseFieldArrayUpdate<FieldValues, string>;
	remove: UseFieldArrayRemove;
	field: Record<'id', string>;
	itemsCount: number;
}

type RenderPropsChildrenType = (
	ctx: IRenderProps,
) => React.ReactElement | React.ReactElement[];

interface FormMultiplierProps {
	children: React.ReactElement | React.ReactElement[] | RenderPropsChildrenType;
	name: string;
	createDefault?: number;
	addButtonTitle?: string;
	allowAddForm?: boolean;
	min?: number;
	max?: number;
	className?: string;
	removeButton?: {
		title: string;
		position: string;
	};
}

const FormMultiplier: React.FC<FormMultiplierProps> = props => {
	const {
		children,
		name,
		createDefault,
		addButtonTitle,
		allowAddForm = true,
		max,
		min,
		className,
		removeButton,
	} = props;
	const { name: formContainerName } = useFormContainerContext();

	const finalName = formContainerName ? `${formContainerName}.${name}` : name;

	const { control } = useFormContext();
	const { fields, append, remove, update } = useFieldArray({
		control,
		name: finalName,
	});

	const defaultCount = min ?? createDefault ?? 0;
	useEffect(() => {
		const fieldsToAdd = defaultCount - fields.length;
		if (fieldsToAdd > 0) {
			const newFields = Array(fieldsToAdd).fill({});
			setTimeout(() => {
				append(newFields);
			}, 0);
		}
	}, [defaultCount]);

	const allowAdd = allowAddForm && (!max || fields.length < max);
	const renderItem = ({
		id,
		isVisible = true,
		index,
		removeButton,
	}: {
		id: string;
		isVisible?: boolean;
		index: number;
		removeButton?: {
			title: string;
			position: string;
		};
	}) => {
		const childName = `${finalName}[${index}]`;

		return (
			<FormMultiplierContextProvider
				onClick={() => handleRemove(index)}
				key={id}
				isVisible={isVisible}>
				<FormContainer name={childName} ignoreFinalName>
					{typeof children === 'function'
						? children({
								index,
								update,
								field: fields[index],
								remove,
								itemsCount: fields.length,
						  })
						: children}
					{removeButton && (
						<div className='d-flex justify-content-end'>
							<FormMultiplierRemoveButton>
								<RemoveButton
									onClick={() => void 0}
									title={removeButton.title}
								/>
							</FormMultiplierRemoveButton>
						</div>
					)}
				</FormContainer>
			</FormMultiplierContextProvider>
		);
	};

	const handleRemove = (id: number) => {
		if (fields.length > (min || 0)) {
			remove(id);
		}
	};

	const handleAdd = () => {
		if (!max || fields.length < max) {
			append({});
		}
	};

	return (
		<div className={className}>
			{fields.map((field, index, array) => {
				const isVisible = array.length > (min || createDefault || 0);

				return renderItem({
					id: field.id,
					isVisible,
					index,
					removeButton,
				});
			})}
			{allowAdd && (
				<div style={{ display: 'flex', marginBottom: '20px' }}>
					<AddButton onClick={handleAdd} title={addButtonTitle} />
				</div>
			)}
		</div>
	);
};

export default FormMultiplier;
